import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchPackageTemplates, createPackageTemplate, sellPackage,
  fetchPackages, redeemPackage, fetchMembers, getCurrency,
} from "../services/api";
import { formatPrice } from "../utils/currency";

// Session packs (gym/spa): sell a bucket of credits (10-class pack, 5x PT) — the
// sale rings through the till — and burn a credit each time a session is used.

const METHODS = ["cash", "telebirr", "cbe_birr", "pos", "other"];

const S = {
  en: {
    title: "Packages", sub: "Sell session packs and track remaining credits.",
    templates: "Package types", new_t: "New type", t_name: "Name", credits: "Credits", price: "Price",
    valid: "Valid (days)", opt: "optional", create: "Create", sell: "Sell a package",
    to: "To", search_m: "Search member…", walkin: "or walk-in name", pick_t: "Package",
    sell_btn: "Sell & charge", method: "Method", sold: "Active packages", none_sold: "No active packages.",
    left: "left", of: "of", used_up: "used up", expires: "expires", use1: "Use 1", holder: "Holder",
    sold_ok: "Package sold ✓", none_t: "No package types yet — create one above.",
  },
  am: {
    title: "ጥቅሎች", sub: "የክፍለ-ጊዜ ጥቅሎችን ይሽጡ እና ቀሪ ክሬዲት ይከታተሉ።",
    templates: "የጥቅል አይነቶች", new_t: "አዲስ አይነት", t_name: "ስም", credits: "ክሬዲቶች", price: "ዋጋ",
    valid: "የሚያገለግል (ቀናት)", opt: "አማራጭ", create: "ፍጠር", sell: "ጥቅል ሽጥ",
    to: "ለ", search_m: "አባል ፈልግ…", walkin: "ወይም የጎብኚ ስም", pick_t: "ጥቅል",
    sell_btn: "ሽጥ እና አስከፍል", method: "ዘዴ", sold: "ንቁ ጥቅሎች", none_sold: "ንቁ ጥቅል የለም።",
    left: "ቀሪ", of: "ከ", used_up: "አልቋል", expires: "የሚያበቃ", use1: "1 ተጠቀም", holder: "ባለቤት",
    sold_ok: "ጥቅል ተሽጧል ✓", none_t: "ገና የጥቅል አይነት የለም — ከላይ ይፍጠሩ።",
  },
  om: {
    title: "Paakejii", sub: "Paakejii sessii gurgurii fi kireeditii hafe hordofi.",
    templates: "Gosa paakejii", new_t: "Gosa haaraa", t_name: "Maqaa", credits: "Kireeditii", price: "Gatii",
    valid: "Kan tajaajilu (guyyaa)", opt: "filannoo", create: "Uumi", sell: "Paakejii gurgurii",
    to: "Kan", search_m: "Miseensa barbaadi…", walkin: "yookiin maqaa keessummaa", pick_t: "Paakejii",
    sell_btn: "Gurgurii & kaffalchiisi", method: "Mala", sold: "Paakejii sochii", none_sold: "Paakejiin sochii hin jiru.",
    left: "hafe", of: "kan", used_up: "dhume", expires: "xumurama", use1: "1 fayyadami", holder: "Abbaa",
    sold_ok: "Paakejiin gurgurame ✓", none_t: "Ammatti gosti paakejii hin jiru — olitti uumi.",
  },
};

export default function PackagesPage() {
  const lang = (typeof window !== "undefined" && localStorage.getItem("lang")) || "en";
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (n) => formatPrice(Number(n || 0), cur);

  const [tmpls, setTmpls] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [members, setMembers] = useState([]);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  const [showT, setShowT] = useState(false);
  const [tf, setTf] = useState({ name: "", credits: 10, price: "", valid_days: "", area: "gym" });
  const [sel, setSel] = useState({ template_id: "", membership_id: "", name: "", method: "cash" });
  const [mq, setMq] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([fetchPackageTemplates(), fetchPackages({ active_only: true })]);
      setTmpls(a); setPkgs(b);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not load packages."); }
  }, []);
  useEffect(() => { load(); fetchMembers().then(setMembers).catch(() => {}); }, [load]);

  const createT = async () => {
    if (!tf.name.trim() || !(Number(tf.credits) > 0)) { setErr("Name and credits are required."); return; }
    try {
      await createPackageTemplate({ name: tf.name.trim(), credits: Number(tf.credits), price: Number(tf.price) || 0,
        valid_days: tf.valid_days ? Number(tf.valid_days) : null, area: tf.area });
      setShowT(false); setTf({ name: "", credits: 10, price: "", valid_days: "", area: "gym" }); await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not create the type."); }
  };

  const sell = async () => {
    if (!sel.template_id && !sel.name.trim()) { setErr("Pick a package type."); return; }
    try {
      const body = { method: sel.method };
      if (sel.template_id) body.template_id = Number(sel.template_id);
      if (sel.membership_id) body.membership_id = Number(sel.membership_id);
      else if (sel.name.trim()) body.name = sel.name.trim();
      await sellPackage(body);
      setFlash(t("sold_ok")); setSel({ template_id: "", membership_id: "", name: "", method: "cash" }); setMq("");
      await load(); setTimeout(() => setFlash(""), 2500);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not sell the package."); }
  };

  const use = async (p) => {
    try { await redeemPackage(p.id, 1); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not use a credit."); }
  };

  const matches = useMemo(() => {
    const s = mq.trim().toLowerCase();
    if (!s) return [];
    return members.filter((m) => (m.name || "").toLowerCase().includes(s) || (m.phone || "").includes(s)).slice(0, 6);
  }, [members, mq]);
  const selMember = members.find((m) => String(m.id) === String(sel.membership_id));

  return (
    <div className="page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div><h1 style={{ marginBottom: 2 }}>🎟️ {t("title")}</h1><p className="muted" style={{ marginTop: 0 }}>{t("sub")}</p></div>
        <button className="btn" onClick={() => setShowT((v) => !v)}>＋ {t("new_t")}</button>
      </div>

      {err && <div style={{ background: "#fdecec", color: "#b02a2a", padding: "8px 12px", borderRadius: 8, margin: "8px 0" }}>{err}</div>}
      {flash && <div style={{ background: "#f2fbf4", color: "#2b8a3e", border: "1px solid #cdeed5", padding: "8px 12px", borderRadius: 8, margin: "8px 0" }}>{flash}</div>}

      {showT && (
        <div style={{ background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: 16, margin: "10px 0" }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div className="field" style={{ flex: "2 1 200px" }}><label>{t("t_name")}</label>
              <input value={tf.name} onChange={(e) => setTf({ ...tf, name: e.target.value })} placeholder="10 Spin classes / 5x PT" style={{ width: "100%", padding: 8 }} /></div>
            <div className="field" style={{ flex: "0 1 90px" }}><label>{t("credits")}</label>
              <input type="number" min="1" value={tf.credits} onChange={(e) => setTf({ ...tf, credits: e.target.value })} style={{ width: "100%", padding: 8 }} /></div>
            <div className="field" style={{ flex: "0 1 110px" }}><label>{t("price")}</label>
              <input type="number" min="0" value={tf.price} onChange={(e) => setTf({ ...tf, price: e.target.value })} style={{ width: "100%", padding: 8 }} /></div>
            <div className="field" style={{ flex: "0 1 110px" }}><label>{t("valid")} <span className="muted">({t("opt")})</span></label>
              <input type="number" min="0" value={tf.valid_days} onChange={(e) => setTf({ ...tf, valid_days: e.target.value })} style={{ width: "100%", padding: 8 }} /></div>
          </div>
          <button className="btn btn-primary" onClick={createT} style={{ marginTop: 8 }}>{t("create")}</button>
        </div>
      )}

      {/* Sell */}
      <div style={{ background: "#fff", border: "1px solid #eef0f3", borderRadius: 12, padding: 16, margin: "10px 0" }}>
        <h3 style={{ marginTop: 0 }}>{t("sell")}</h3>
        {tmpls.length === 0 && <p className="muted">{t("none_t")}</p>}
        {tmpls.length > 0 && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="field" style={{ flex: "1 1 200px" }}><label>{t("pick_t")}</label>
              <select value={sel.template_id} onChange={(e) => setSel({ ...sel, template_id: e.target.value })} style={{ width: "100%", padding: 8 }}>
                <option value="">—</option>
                {tmpls.filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{x.name} · {x.credits} · {money(x.price)}</option>)}
              </select></div>
            <div className="field" style={{ flex: "1 1 200px" }}><label>{t("to")}</label>
              {selMember ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, border: "1px solid #d3d8de", borderRadius: 8 }}>
                  <span style={{ flex: 1 }}>{selMember.name}</span>
                  <button className="btn" onClick={() => { setSel({ ...sel, membership_id: "" }); setMq(""); }} style={{ padding: "2px 8px" }}>✕</button>
                </div>
              ) : (
                <>
                  <input value={mq} onChange={(e) => setMq(e.target.value)} placeholder={t("search_m")} style={{ width: "100%", padding: 8 }} />
                  {matches.map((m) => (
                    <div key={m.id} onClick={() => { setSel({ ...sel, membership_id: m.id, name: "" }); setMq(""); }} style={{ cursor: "pointer", padding: "4px 6px" }}>{m.name} <span className="muted" style={{ fontSize: 12 }}>{m.plan_name}</span></div>
                  ))}
                  {!mq && <input value={sel.name} onChange={(e) => setSel({ ...sel, name: e.target.value })} placeholder={t("walkin")} style={{ width: "100%", padding: 8, marginTop: 4 }} />}
                </>
              )}
            </div>
            <div className="field" style={{ flex: "0 1 130px" }}><label>{t("method")}</label>
              <select value={sel.method} onChange={(e) => setSel({ ...sel, method: e.target.value })} style={{ width: "100%", padding: 8 }}>
                {METHODS.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
              </select></div>
            <button className="btn btn-primary" onClick={sell} style={{ padding: "9px 14px" }}>{t("sell_btn")}</button>
          </div>
        )}
      </div>

      {/* Active packages */}
      <h3 style={{ marginBottom: 8 }}>{t("sold")}</h3>
      {pkgs.length === 0 && <p className="muted">{t("none_sold")}</p>}
      {pkgs.map((p) => (
        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: "1px solid #eef0f3", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
          <div style={{ flex: "1 1 180px" }}>
            <div style={{ fontWeight: 600 }}>{p.holder || "—"} <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>· {p.name}</span></div>
            <div className="muted" style={{ fontSize: 12 }}>{p.expires_at ? `${t("expires")} ${p.expires_at}` : ""}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: p.remaining_credits <= 1 ? "#b02a2a" : "#1f2d3d" }}>{p.remaining_credits}</div>
            <div className="muted" style={{ fontSize: 11 }}>{t("left")} {t("of")} {p.total_credits}</div>
          </div>
          <button className="btn btn-primary" disabled={p.remaining_credits <= 0} onClick={() => use(p)} style={{ padding: "6px 12px" }}>{t("use1")}</button>
        </div>
      ))}
    </div>
  );
}
